/**
 * APPENDIX A — Detailed Findings Justification & Supporting Evidence
 *
 * Renders one "case file" card per finding in Section 09 remediation roadmap
 * order. Each card includes a header (Finding A-NN · F-###), a metadata grid
 * (Finding ID / Control ID / CSV Domain / NIST Function / Timeframe), a
 * compact Risk · Priority · Timeframe strip, a cross-reference back to
 * Section 09, and four narrative blocks: Justification, Observed Gap,
 * Evidence Reviewed, Recommended Additional Evidence.
 */
import type jsPDF from "jspdf";
import { PAGE_MARGIN, REPORT_THEME, type RGB } from "./theme";
import { drawRunningHeader, drawPageFooter } from "./pageChrome";
import type { EvidenceAppendixRow } from "./renderEvidenceAppendix";

export interface RoadmapOrderRow {
  id?: string | null;
  display_id?: string | null;
  display_seq?: number | null;
  control_code?: string | null;
  finding_title?: string | null;
  risk_level?: string | null;
  priority_level?: string | null;
  timeframe?: string | null;
  csv_domain?: string | null;
  nist_function?: string | null;
  due_date?: string | null;
}

export interface DetailedFindingsAppendixContext {
  evidenceRows: EvidenceAppendixRow[];
  roadmapRows: RoadmapOrderRow[];
  clientOrgName: string;
  methodology?: string | null;
  frameworks?: string[] | null;
  startPageNumber: number;
}

// ── Sort weights mirror Section 09 ────────────────────────────────
const PRIORITY_WEIGHT: Record<string, number> = {
  Immediate: 0, "Quick Win": 1, Strategic: 2,
};
const RISK_WEIGHT: Record<string, number> = {
  Critical: 0, High: 1, Moderate: 2, Medium: 2, Low: 3,
};

const PRIORITY_SECTION_LABEL: Record<string, string> = {
  Immediate: "Immediate Actions",
  "Quick Win": "Quick Wins",
  Strategic: "Strategic Initiatives",
};

const RISK_VALUE_COLOR: Record<string, RGB> = {
  Critical: [153, 27, 27],
  High:     [220, 38, 38],
  Moderate: [202, 138, 4],
  Low:      [71, 85, 105],
};
const PRIORITY_VALUE_COLOR: Record<string, RGB> = {
  Immediate:   [220, 38, 38],
  "Quick Win": [202, 138, 4],
  Strategic:   [37, 99, 235],
};
const RIBBON_BG: RGB = [243, 245, 249];
const RIBBON_BORDER: RGB = [222, 228, 238];

function normRisk(v?: string | null): string {
  if (!v) return "";
  const s = String(v).trim().toLowerCase();
  if (s === "critical") return "Critical";
  if (s === "high") return "High";
  if (s === "moderate" || s === "medium") return "Moderate";
  if (s === "low") return "Low";
  return String(v).trim();
}
function normPriority(v?: string | null): string {
  if (!v) return "";
  const s = String(v).trim().toLowerCase();
  if (s === "immediate") return "Immediate";
  if (s === "quick win") return "Quick Win";
  if (s === "strategic") return "Strategic";
  return String(v).trim();
}

interface OrderedFinding {
  id: string;
  display_id: string;
  control_code: string;
  finding_title: string;
  risk_level: string;
  priority_level: string;
  timeframe: string;
  csv_domain: string;
  nist_function: string;
  due_date: string | null;
}

function dueTs(v?: string | null): number {
  if (!v) return Number.POSITIVE_INFINITY;
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime();
  const t = new Date(v).getTime();
  return isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

function buildOrder(rows: RoadmapOrderRow[]): OrderedFinding[] {
  const seen = new Set<string>();
  const out: OrderedFinding[] = [];
  for (const r of rows) {
    const key = `${(r.finding_title ?? "").toLowerCase().trim()}|${(r.control_code ?? "").toLowerCase().trim()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      id: r.id ?? "",
      display_id: (r.display_id ?? "").trim(),
      control_code: r.control_code ?? "",
      finding_title: r.finding_title ?? "(Untitled finding)",
      risk_level: normRisk(r.risk_level),
      priority_level: normPriority(r.priority_level),
      timeframe: r.timeframe ?? "",
      csv_domain: r.csv_domain ?? "",
      nist_function: r.nist_function ?? "",
      due_date: r.due_date ?? null,
    });
  }
  // Match Findings tab / CAP / Roadmap ordering:
  // due_date ASC (nulls last) → priority → risk → csv_domain.
  out.sort((a, b) => {
    const ta = dueTs(a.due_date);
    const tb = dueTs(b.due_date);
    if (ta !== tb) return ta - tb;
    const pa = PRIORITY_WEIGHT[a.priority_level] ?? 9;
    const pb = PRIORITY_WEIGHT[b.priority_level] ?? 9;
    if (pa !== pb) return pa - pb;
    const ra = RISK_WEIGHT[a.risk_level] ?? 9;
    const rb = RISK_WEIGHT[b.risk_level] ?? 9;
    if (ra !== rb) return ra - rb;
    return (a.csv_domain || "").localeCompare(b.csv_domain || "");
  });
  return out;
}

interface EvidenceGroup {
  justification: string;
  observed_gap: string;
  legacy_evidence_reviewed: string;
  recommended_additional_evidence: string;
  evidence: EvidenceAppendixRow[];
}

function groupEvidence(rows: EvidenceAppendixRow[]): Map<string, EvidenceGroup> {
  const map = new Map<string, EvidenceGroup>();
  for (const r of rows) {
    if (!r.finding_id) continue;
    let g = map.get(r.finding_id);
    if (!g) {
      g = {
        justification: r.justification ?? "",
        observed_gap: r.observed_gap ?? "",
        legacy_evidence_reviewed: r.evidence_reviewed ?? "",
        recommended_additional_evidence: r.recommended_additional_evidence ?? "",
        evidence: [],
      };
      map.set(r.finding_id, g);
    }
    if (r.evidence_id) g.evidence.push(r);
  }
  return map;
}

interface EvidenceItem {
  label: string;
  inferred: boolean;
}

function buildEvidenceItems(g: EvidenceGroup | undefined): EvidenceItem[] {
  if (!g) return [];
  // Appendix A — Evidence Reviewed shows ONLY reviewer-curated submitted
  // evidence (explicitly linked via the Findings tab). Auto/inferred
  // question-level evidence is intentionally excluded so the report reflects
  // what the reviewer actually evaluated.
  const explicit = g.evidence.filter((e) => e.link_kind === "explicit");
  if (explicit.length > 0) {
    return explicit.map((e) => ({
      // Title-only display. URL, source, and description are intentionally
      // hidden from the report; reviewers can see them in the console.
      label: (e.title && e.title.trim()) || "Untitled evidence item",
      inferred: false,
    }));
  }
  // Legacy fallback: pre-evidence-link reviewers wrote a free-form
  // "Evidence Reviewed" string. Render it only when no explicit links exist.
  const text = (g.legacy_evidence_reviewed ?? "").trim();
  if (!text) return [];
  return text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((l) => ({
    label: l.replace(/^[-•]\s*/, ""),
    inferred: false,
  }));
}

function splitBullets(text: string | undefined | null): string[] {
  if (!text) return [];
  return text
    .split(/\r?\n|;|·/)
    .map((l) => l.trim().replace(/^[-•]\s*/, ""))
    .filter(Boolean);
}

function appendixIndex(n: number): string {
  return `Finding A-${String(n).padStart(2, "0")}`;
}

// ── Title bar at top of appendix-A pages ──────────────────────────
function drawAppendixTitle(doc: jsPDF, continued: boolean): number {
  const baseY = 86;
  const prefix = `APPENDIX A${continued ? " (cont'd)" : ""}: `;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10.5);
  doc.setTextColor(...REPORT_THEME.gold.rgb);
  doc.text(prefix, PAGE_MARGIN, baseY);
  const prefixW = doc.getTextWidth(prefix);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(...REPORT_THEME.navy.rgb);
  doc.text("Detailed Findings Justification & Supporting Evidence", PAGE_MARGIN + prefixW, baseY);

  doc.setDrawColor(...REPORT_THEME.gold.rgb);
  doc.setLineWidth(1.4);
  doc.line(PAGE_MARGIN, baseY + 8, PAGE_MARGIN + 64, baseY + 8);
  return baseY + 26;
}

function drawBadge(
  doc: jsPDF, x: number, y: number,
  label: string, fill: RGB, textColor: RGB,
): number {
  if (!label) return x;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  const padX = 6;
  const h = 13;
  const tw = doc.getTextWidth(label);
  const w = tw + padX * 2;
  doc.setFillColor(...fill);
  doc.roundedRect(x, y - h + 3, w, h, 2.5, 2.5, "F");
  doc.setTextColor(...textColor);
  doc.text(label, x + padX, y);
  return x + w;
}

export function renderDetailedFindingsAppendix(
  doc: jsPDF,
  ctx: DetailedFindingsAppendixContext,
): number {
  const ordered = buildOrder(ctx.roadmapRows ?? []);
  if (ordered.length === 0) return ctx.startPageNumber - 1;
  const evidenceMap = groupEvidence(ctx.evidenceRows ?? []);

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const innerW = pageW - PAGE_MARGIN * 2;
  const bottomLimit = pageH - 60;
  const topY = 112; // y after appendix title bar

  // Card styling
  const CARD_PAD_X = 14;
  const CARD_PAD_Y = 14;
  const CARD_RADIUS = 6;
  const CARD_INNER_W = innerW - CARD_PAD_X * 2;
  const CARD_BG: RGB = [251, 252, 254];
  const CARD_BORDER: RGB = [222, 228, 238];

  let pageNumber = ctx.startPageNumber;
  doc.addPage();
  drawRunningHeader(doc, {
    clientName: ctx.clientOrgName,
    frameworks: ctx.frameworks ?? null,
    methodology: ctx.methodology ?? null,
  });
  let y = drawAppendixTitle(doc, false);

  // Intro
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...REPORT_THEME.textMuted.rgb);
  const intro =
    "This appendix provides the supporting rationale, identified gaps, evidence reviewed, and " +
    "additional evidence recommendations associated with each finding referenced in Section 09. " +
    "The information below documents the assessment basis used to support reviewer conclusions " +
    "and remediation recommendations.";
  const introLines = doc.splitTextToSize(intro, innerW);
  doc.text(introLines, PAGE_MARGIN, y);
  y += introLines.length * 12 + 18;


  // ── Pagination helpers ──────────────────────────────────────────
  const newPage = () => {
    drawPageFooter(doc, pageNumber);
    pageNumber += 1;
    doc.addPage();
    drawRunningHeader(doc, {
      clientName: ctx.clientOrgName,
      frameworks: ctx.frameworks ?? null,
      methodology: ctx.methodology ?? null,
    });
    y = drawAppendixTitle(doc, true);
  };

  // ── Pre-measure each finding card height ────────────────────────
  const HEADER_BLOCK_H = (f: OrderedFinding) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    const titleLines = doc.splitTextToSize(f.finding_title, CARD_INNER_W).length;
    // refLabel + title + domain•control + gold underline + xref + padding
    return 14 + titleLines * 16 + 14 + 10 + 16 + 6;
  };
  const META_H = 30 + 12;
  const RIBBON_H = 26;
  const REMEDIATION_REF_H = 18;

  const blockHeight = (label: string, lines: number) => {
    if (lines <= 0) return 0;
    return 14 /* header */ + lines * 13 + 12;
  };

  const measureBlocks = (f: OrderedFinding, g: EvidenceGroup | undefined) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const just = (g?.justification ?? "").trim() || "No justification provided.";
    const gap = (g?.observed_gap ?? "").trim() || "Not documented.";
    const evItems = buildEvidenceItems(g);
    const recBullets = splitBullets(g?.recommended_additional_evidence);

    const justLines = doc.splitTextToSize(just, CARD_INNER_W).length;
    const gapLines = doc.splitTextToSize(gap, CARD_INNER_W).length;
    let evLines = 0;
    if (evItems.length === 0) {
      evLines = 1;
    } else {
      for (const it of evItems) {
        evLines += doc.splitTextToSize(`• ${it.label}`, CARD_INNER_W - 10).length;
      }
    }
    let recLines = 0;
    if (recBullets.length === 0) {
      recLines = 1;
    } else {
      for (const b of recBullets) {
        recLines += doc.splitTextToSize(`• ${b}`, CARD_INNER_W - 10).length;
      }
    }
    return (
      blockHeight("Justification", justLines) +
      blockHeight("Observed Gap", gapLines) +
      blockHeight("Evidence Reviewed", evLines) +
      blockHeight("Recommended Additional Evidence", recLines)
    );
  };


  // ── Render one card ─────────────────────────────────────────────
  for (let i = 0; i < ordered.length; i++) {
    const f = ordered[i];
    const evidence = evidenceMap.get(f.id);
    const refLabel = `${appendixIndex(i + 1)}${f.display_id ? `  ·  ${f.display_id}` : ""}`;

    const headerH = HEADER_BLOCK_H(f);
    const bodyH = measureBlocks(f, evidence);
    const cardH = CARD_PAD_Y * 2 + headerH + META_H + RIBBON_H + bodyH + REMEDIATION_REF_H;

    const remaining = bottomLimit - y;
    const pageSpace = bottomLimit - topY;

    // Pagination: if card fits → draw on this page.
    // Else if remaining < 40% of card height (and card itself fits a page) → move to next page.
    // Else flow.
    let drawAsUnified = false;
    if (cardH <= remaining) {
      drawAsUnified = true;
    } else if (cardH <= pageSpace) {
      // card fits on a fresh page
      if (remaining < cardH * 0.4) {
        newPage();
      }
      // try again after possible newPage
      if (cardH <= bottomLimit - y) drawAsUnified = true;
    } else {
      // card too tall for any page — flow with per-page slices
      if (remaining < pageSpace * 0.3) newPage();
    }
    void drawAsUnified;

    // Helper to draw card background/border for a slice (yStart..y on current page)
    const cardSlices: Array<{ page: number; yStart: number }> = [];
    cardSlices.push({ page: pageNumber, yStart: y });

    const xCard = PAGE_MARGIN;
    const xContent = xCard + CARD_PAD_X;
    void xCard;
    y += CARD_PAD_Y;

    // ── Header: ref label + title + domain•control + gold underline + xref ───
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...REPORT_THEME.gold.rgb);
    doc.text(refLabel, xContent, y + 8);
    y += 14;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(...REPORT_THEME.navy.rgb);
    const titleLines = doc.splitTextToSize(f.finding_title, CARD_INNER_W);
    for (const ln of titleLines) {
      doc.text(ln, xContent, y + 12);
      y += 16;
    }

    // Domain • Control inline orientation line
    const domainControlParts: string[] = [];
    if (f.csv_domain) domainControlParts.push(f.csv_domain);
    if (f.control_code) domainControlParts.push(f.control_code);
    if (domainControlParts.length > 0) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...REPORT_THEME.accent.rgb);
      doc.text(domainControlParts.join("  •  "), xContent, y + 10);
      y += 14;
    }

    doc.setDrawColor(...REPORT_THEME.gold.rgb);
    doc.setLineWidth(1.2);
    doc.line(xContent, y + 2, xContent + 48, y + 2);
    y += 10;

    const sectionLabel = PRIORITY_SECTION_LABEL[f.priority_level] ?? "Remediation Roadmap";
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8.5);
    doc.setTextColor(...REPORT_THEME.textMuted.rgb);
    doc.text(`Referenced in Section 09 – ${sectionLabel}`, xContent, y + 10);
    y += 18;

    // ── Metadata grid ───────────────────────────────────────────────
    const metaCellH = 30;
    const metaY = y;
    doc.setFillColor(...REPORT_THEME.surfaceAlt.rgb);
    doc.setDrawColor(...REPORT_THEME.border.rgb);
    doc.setLineWidth(0.5);
    doc.roundedRect(xContent, metaY, CARD_INNER_W, metaCellH, 4, 4, "FD");

    const metaItems: Array<{ label: string; value: string }> = [
      { label: "FINDING ID",   value: f.display_id || "—" },
      { label: "CONTROL ID",   value: f.control_code || "—" },
      { label: "CSV DOMAIN",   value: f.csv_domain || "—" },
      { label: "NIST FUNCTION", value: f.nist_function || "—" },
      { label: "TIMEFRAME",    value: f.timeframe || "—" },
    ];
    const colW = CARD_INNER_W / metaItems.length;
    metaItems.forEach((it, idx) => {
      const cx = xContent + idx * colW + 10;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(6.8);
      doc.setTextColor(...REPORT_THEME.textMuted.rgb);
      doc.text(it.label, cx, metaY + 11);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(...REPORT_THEME.navy.rgb);
      const v = doc.splitTextToSize(it.value, colW - 14)[0] ?? "—";
      doc.text(v, cx, metaY + 24);
    });
    y = metaY + metaCellH + 12;

    // ── Risk · Priority · Timeframe ribbon ──────────────────────────
    const ribbonH = 22;
    const ribbonY = y;
    doc.setFillColor(...RIBBON_BG);
    doc.setDrawColor(...RIBBON_BORDER);
    doc.setLineWidth(0.5);
    doc.roundedRect(xContent, ribbonY, CARD_INNER_W, ribbonH, 3, 3, "FD");

    const ribbonTextY = ribbonY + 14;
    type RibbonSeg = { label: string; value: string; color: RGB };
    const segs: RibbonSeg[] = [];
    if (f.risk_level) {
      segs.push({
        label: "Risk:",
        value: f.risk_level.toUpperCase(),
        color: RISK_VALUE_COLOR[f.risk_level] ?? REPORT_THEME.navy.rgb,
      });
    }
    if (f.priority_level) {
      segs.push({
        label: "Priority:",
        value: f.priority_level.toUpperCase(),
        color: PRIORITY_VALUE_COLOR[f.priority_level] ?? REPORT_THEME.navy.rgb,
      });
    }
    if (f.timeframe) {
      segs.push({
        label: "Timeframe:",
        value: f.timeframe,
        color: REPORT_THEME.navy.rgb,
      });
    }

    // Measure total content width to apply consistent spacing
    let rx = xContent + 12;
    segs.forEach((s, idx) => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(...REPORT_THEME.navy.rgb);
      doc.text(s.label, rx, ribbonTextY);
      rx += doc.getTextWidth(s.label) + 5;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(...s.color);
      doc.text(s.value, rx, ribbonTextY);
      rx += doc.getTextWidth(s.value);

      if (idx < segs.length - 1) {
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...REPORT_THEME.border.rgb);
        rx += 10;
        doc.text("|", rx, ribbonTextY);
        rx += 10;
      }
    });
    y = ribbonY + ribbonH + 14;

    // ── Font state helpers ──────────────────────────────────────────
    // Re-apply the body font state every time we may have crossed a page
    // break. Without this, drawAppendixTitle() inside newPage() leaves
    // the doc in bold 15pt navy, and the next body line renders in the
    // title style (observed as oversized bold navy continuation text in
    // Appendix A).
    const applyBodyTextStyle = () => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
    };
    const applyBlockHeadingStyle = () => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);
      doc.setTextColor(...REPORT_THEME.navy.rgb);
    };

    // ── Block writer with page-break support ────────────────────────
    const ensureSpaceFlow = (needed: number): boolean => {
      if (y + needed > bottomLimit) {
        // close current slice, open new page, start new slice
        finalizeSlices(cardSlices, bottomLimit - CARD_PAD_Y * 0 /* close at slice bottom */);
        newPage();
        cardSlices.push({ page: pageNumber, yStart: y });
        y += CARD_PAD_Y;
        return true;
      }
      return false;
    };

    const writeBlock = (label: string, lines: string[]) => {
      ensureSpaceFlow(20);
      applyBlockHeadingStyle();
      doc.text(label.toUpperCase(), xContent, y);
      doc.setDrawColor(...REPORT_THEME.gold.rgb);
      doc.setLineWidth(0.9);
      doc.line(xContent, y + 3, xContent + 28, y + 3);
      y += 16;

      applyBodyTextStyle();
      for (const ln of lines) {
        const broke = ensureSpaceFlow(13);
        // Always re-apply body style if we crossed a page boundary —
        // drawAppendixTitle() in newPage() clobbers font/size/weight/color.
        if (broke) applyBodyTextStyle();
        doc.text(ln, xContent, y);
        y += 13;
      }
      y += 12;
    };

    // Justification
    const just = (evidence?.justification ?? "").trim() || "No justification provided.";
    writeBlock("Justification / Rationale", doc.splitTextToSize(just, CARD_INNER_W));

    // Observed Gap (always)
    const gap = (evidence?.observed_gap ?? "").trim() || "Not documented.";
    writeBlock("Observed Gap", doc.splitTextToSize(gap, CARD_INNER_W));

    // Evidence Reviewed
    const evItems = buildEvidenceItems(evidence);
    const evidenceLines: string[] = [];
    if (evItems.length === 0) {
      evidenceLines.push("No evidence linked to this finding.");
    } else {
      for (const it of evItems) {
        const wrapped = doc.splitTextToSize(`• ${it.label}`, CARD_INNER_W - 10);
        evidenceLines.push(...wrapped);
      }
    }
    writeBlock("Evidence Reviewed", evidenceLines);

    // Recommended Additional Evidence
    const recBullets = splitBullets(evidence?.recommended_additional_evidence);
    const recLines: string[] = [];
    if (recBullets.length === 0) {
      recLines.push("No additional evidence recommendations were provided.");
    } else {
      for (const b of recBullets) {
        const wrapped = doc.splitTextToSize(`• ${b}`, CARD_INNER_W - 10);
        recLines.push(...wrapped);
      }
    }
    writeBlock("Recommended Additional Evidence", recLines);

    // ── Remediation cross-reference ─────────────────────────────────
    ensureSpaceFlow(REMEDIATION_REF_H);
    const sectionPart = PRIORITY_SECTION_LABEL[f.priority_level] ?? null;
    const remTarget = [
      "Section 09",
      sectionPart,
      f.display_id || null,
    ].filter(Boolean).join(" – ");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...REPORT_THEME.textMuted.rgb);
    doc.text("REMEDIATION REFERENCE:", xContent, y + 2);
    const labelW = doc.getTextWidth("REMEDIATION REFERENCE:") + 6;
    // Muted metadata styling — matches Control ID / CSV Domain / NIST Function / Timeframe.
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
    doc.text(remTarget, xContent + labelW, y + 2);
    y += 14;

    // Close card: add bottom padding then draw bg/border for all slices
    y += CARD_PAD_Y - 4;
    finalizeSlices(cardSlices, y);

    // Spacing between cards
    y += 20;
  }


  drawPageFooter(doc, pageNumber);
  return pageNumber;

  // ── Inner: draw card background+border for accumulated slices ────
  function finalizeSlices(
    slices: Array<{ page: number; yStart: number }>,
    yEndCurrent: number,
  ) {
    const currentPageIndex = doc.getNumberOfPages();
    for (let s = 0; s < slices.length; s++) {
      const slice = slices[s];
      const isLast = s === slices.length - 1;
      const yEnd = isLast ? yEndCurrent : bottomLimit;
      const pageOffset = currentPageIndex - (pageNumber - slice.page) - 0;
      // We navigate by absolute page index. Compute: pageNumber maps to currentPageIndex.
      const targetPageIdx = currentPageIndex - (pageNumber - slice.page);
      doc.setPage(targetPageIdx);
      doc.setFillColor(...CARD_BG);
      doc.setDrawColor(...CARD_BORDER);
      doc.setLineWidth(0.6);
      const h = Math.max(8, yEnd - slice.yStart);
      doc.roundedRect(PAGE_MARGIN, slice.yStart, innerW, h, CARD_RADIUS, CARD_RADIUS, "S");
      // Note: we draw stroke only (no fill) to avoid covering already-drawn content.
      void pageOffset;
    }
    // restore to current page
    doc.setPage(currentPageIndex);
    // clear slices array in-place so caller's reference is reset for next card
    slices.length = 0;
  }
}
